/**
 * @fileoverview County Selector Component
 * @module CountySelector
 * 
 * Business Context:
 * - Provides user-friendly county selection for Florida properties
 * - Supports both dropdown and searchable modes
 * - Eliminates the need for complex ZIP-to-county mapping
 * 
 * Design Context:
 * - Consistent with TBYT form styling (Montserrat + blue/gray system)
 * - Responsive design with search functionality
 * - Clear error states and validation feedback
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, MapPin, Check } from 'lucide-react';
import { apiClient } from '../../api/client';

interface County {
  id: number;
  name: string;
  state: string;
}

interface CountySelectorProps {
  value?: number | null;
  onChange: (countyId: number | null, county: County | null) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  searchable?: boolean;
  className?: string;
}

export const CountySelector: React.FC<CountySelectorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  placeholder = "Select your county",
  required = false,
  searchable = true,
  className = ""
}) => {
  const [counties, setCounties] = useState<County[]>([]);
  const [filteredCounties, setFilteredCounties] = useState<County[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load all counties on mount
  useEffect(() => {
    loadCounties();
  }, []);

  // Find selected county when value changes
  useEffect(() => {
    if (value && counties.length > 0) {
      const county = counties.find(c => c.id === value);
      setSelectedCounty(county || null);
    } else {
      setSelectedCounty(null);
    }
  }, [value, counties]);

  // Filter counties based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCounties(counties);
    } else {
      const filtered = counties.filter(county =>
        county.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCounties(filtered);
    }
  }, [searchTerm, counties]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCounties = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.properties.getCounties();
      if (response.data.success) {
        setCounties(response.data.data);
        setFilteredCounties(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load counties:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (county: County) => {
    setSelectedCounty(county);
    onChange(county.id, county);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setSelectedCounty(null);
    onChange(null, null);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen && searchable) {
      // Focus search input when opening
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Button/Input */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-4 py-3 border rounded-lg text-left
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          transition-colors duration-200 flex items-center justify-between
          ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'}
        `}
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span className={selectedCounty ? 'text-gray-900' : 'text-gray-500'}>
            {selectedCounty ? selectedCounty.name : placeholder}
            {required && !selectedCounty && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {/* Search Input */}
          {searchable && (
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search counties..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                />
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="p-3 text-center text-gray-500" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Loading counties...
              </div>
            ) : filteredCounties.length === 0 ? (
              <div className="p-3 text-center text-gray-500" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {searchTerm ? 'No counties found' : 'No counties available'}
              </div>
            ) : (
              <>
                {/* Clear option */}
                {selectedCounty && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 border-b border-gray-100"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <div className="w-4 h-4"></div>
                    <span className="text-gray-500 italic">Clear selection</span>
                  </button>
                )}
                
                {/* County options */}
                {filteredCounties.map((county) => (
                  <button
                    key={county.id}
                    type="button"
                    onClick={() => handleSelect(county)}
                    className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center space-x-2 transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      {selectedCounty?.id === county.id && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <span className={`${selectedCounty?.id === county.id ? 'text-blue-600 font-medium' : 'text-gray-900'}`}>
                      {county.name}
                    </span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          {error}
        </p>
      )}
    </div>
  );
};